import React, { useCallback, type ReactNode, type RefObject } from 'react';
import { ModalTrigger } from '@atlassian/entry-points/modal-trigger';
import { aiPlannerIframeModalEntryPoint } from './aiPlannerIframeModalEntryPoint.entrypoint.tsx';
import { useAiPlannerIframe } from './AiPlannerIframeProvider.tsx';
import { useAiPlannerIframeLoadExperience } from './useAiPlannerIframeLoadExperience.tsx';
import { useAiPlannerIframeModalProps } from './useAiPlannerIframeModalProps.tsx';

const EMPTY_ENTRY_POINT_PROPS = {} as const;

type TriggerRenderProps = {
	ref?: RefObject<HTMLElement>;
	onClick?: (event: unknown) => void;
	[key: string]: unknown;
};

type AiPlannerIframeModalMenuItemWrapperProps = {
	children: (props?: { triggerProps?: TriggerRenderProps }) => ReactNode;
};

/**
 * Adapts the planner EntryPoint modal trigger to Universal Create's menu-item
 * wrapper contract, so the menu item can open the same shared-iframe modal
 * flow as the planner overview without owning modal state locally.
 */
export const AiPlannerIframeModalMenuItemWrapper = ({
	children,
}: AiPlannerIframeModalMenuItemWrapperProps): React.JSX.Element => {
	const shared = useAiPlannerIframe();
	const modalProps = useAiPlannerIframeModalProps();
	const loadExperience = useAiPlannerIframeLoadExperience({ kind: 'create', surface: 'modal' });
	const handleEntryPointError = useCallback(
		({ error }: { error: Error }) => {
			loadExperience.start();
			loadExperience.fail('entry-point', error, { skipErrorAnalytics: true });
		},
		[loadExperience],
	);
	const handleEntryPointClose = useCallback(() => {
		shared?.releaseActiveClaim();
	}, [shared]);

	return (
		<ModalTrigger
			entryPoint={aiPlannerIframeModalEntryPoint}
			entryPointProps={{ ...EMPTY_ENTRY_POINT_PROPS, onClose: handleEntryPointClose }}
			modalProps={modalProps}
			onError={handleEntryPointError}
		>
			{(triggerProps: TriggerRenderProps) => children({ triggerProps })}
		</ModalTrigger>
	);
};
